import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Leaf } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { formatUAH, cn } from '../lib/format';
import { buildImageSrcSet, optimizeImageUrl } from '../lib/image';

const BadgePill = ({ kind }) => {
  if (!kind) return null;
  const map = {
    'Хіт': { cls: 'bg-[var(--msa-ink)] text-white', text: 'ХІТ' },
    'Новинка': { cls: 'bg-[#0f6fb8] text-white', text: 'НОВЕ' },
    'Рекомендуємо': { cls: 'bg-[#256b38] text-white', text: 'ТОП' },
  };
  const conf = map[kind] || { cls: 'bg-[var(--msa-ink)] text-white', text: kind };
  return (
    <span className={cn('text-[10px] sm:text-[10.5px] font-bold uppercase tracking-wider px-2 sm:px-2.5 py-1 rounded-md sm:rounded-full', conf.cls)}>
      {conf.text}
    </span>
  );
};

const ProductCard = ({ product, priority = false, className, compact = false }) => {
  const { addItem, toggleWishlist, isWished } = useCart();
  const liked = isWished(product);
  const discount = product.oldPrice ? Math.round((1 - product.price / product.oldPrice) * 100) : 0;
  const imageSrc = optimizeImageUrl(product.image, compact ? 320 : 400, compact ? 72 : 68);
  const imageSrcSet = buildImageSrcSet(
    product.image,
    compact ? [160, 240, 320, 400] : [160, 240, 320, 400, 640, 800],
    compact ? [58, 64, 70, 74] : [52, 60, 66, 70, 78, 82],
  );
  const hasPartner = product.brand || (product.categories || []).includes('partner');
  // Наявність приходить з API в обох режимах, але картка її досі ігнорувала:
  // відсутній товар виглядав як звичайний, клався в кошик, і відмова
  // прилітала аж на «Підтвердити замовлення» — після заповнення всієї форми.
  const outOfStock = product.inStock === false;

  return (
    <div className={cn(
      'lift-card group relative bg-white border border-[var(--msa-line)] rounded-2xl flex flex-col',
      compact ? 'p-2.5 lg:p-3 h-full rounded-xl' : 'p-3 sm:p-4 h-full rounded-2xl',
      className,
    )}>
      {/* badges */}
      <div className="absolute top-3 left-3 right-3 flex items-start justify-between z-10 pointer-events-none">
        <div className="flex flex-col gap-1.5 pointer-events-auto">
          {outOfStock
            ? <span className="text-[10px] sm:text-[10.5px] font-bold tracking-wider px-2 sm:px-2.5 py-1 rounded-md sm:rounded-full bg-[var(--msa-line)] text-[var(--msa-ink-soft)]">Немає в наявності</span>
            : <BadgePill kind={product.badge} />}
          {discount > 0 && (
            <span className="text-[10px] sm:text-[10.5px] font-bold tracking-wider px-2 sm:px-2.5 py-1 rounded-md sm:rounded-full bg-[#fff4e0] text-[#a25500]">−{discount}%</span>
          )}
        </div>
        <button
          onClick={(e) => { e.preventDefault(); toggleWishlist(product.id, product); }}
          className={cn('pointer-events-auto w-9 h-9 rounded-full grid place-items-center transition-all border', liked ? 'bg-[var(--msa-green-cta)] border-[var(--msa-green)] text-white' : 'bg-white border-[var(--msa-line)] text-[var(--msa-ink-soft)] hover:text-[var(--msa-green)] hover:border-[var(--msa-green)]')}
          aria-label="До бажань"
        >
          <Heart className={cn('w-4 h-4', liked && 'fill-white')} />
        </button>
      </div>

      <Link
        to={`/product/${product.id}`}
        className={cn(
          'block w-full rounded-lg overflow-hidden bg-[var(--msa-bg-soft)]',
          compact ? 'aspect-[5/6] mb-2' : 'aspect-square rounded-xl mb-4',
        )}
      >
        <img
          src={imageSrc}
          srcSet={imageSrcSet}
          sizes="(max-width: 640px) calc(50vw - 24px), (max-width: 1024px) calc(33vw - 24px), 240px"
          alt={product.name}
          loading={priority ? 'eager' : 'lazy'}
          fetchpriority={priority ? 'high' : undefined}
          decoding="async"
          width="640"
          height="640"
          className={cn(
            'w-full h-full object-cover transition-transform duration-500 will-change-transform group-hover:scale-[1.04]',
            outOfStock && 'opacity-45 saturate-50',
          )}
        />
      </Link>

      {hasPartner && (
        <Link
          to="/partners"
          className={cn(
            'inline-flex items-center gap-1 rounded-full border border-[var(--msa-green-dark)]/30 bg-[var(--msa-green-soft)] text-[var(--msa-green-dark)] font-bold tracking-wide',
            compact ? 'mb-1.5 px-2 py-0.5 text-[9.5px]' : 'mb-2 px-2.5 py-1 text-[10.5px]',
          )}
        >
          <Leaf className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} /> Партнерські продукти
        </Link>
      )}
      <div className={cn(
        'tracking-wider uppercase text-[var(--msa-ink-soft)]/80 font-semibold',
        compact ? 'text-[10px] mb-0.5' : 'text-[11px] mb-1',
      )}
      >
        Артикул: {product.sku}
      </div>
      <Link
        to={`/product/${product.id}`}
        className={cn(
          'font-semibold leading-snug text-[var(--msa-ink)] hover:text-[var(--msa-green)] transition-colors line-clamp-2',
          compact ? 'text-[13px]' : 'text-[14.5px] sm:text-[15px] min-h-[2.5rem] sm:min-h-[2.75rem]',
        )}
      >
        {product.name}
      </Link>
      {!compact && (
        <div className="text-[12px] text-[var(--msa-ink-soft)] mt-1 line-clamp-1 min-h-[1.25rem]">{product.nameEn || '\u00A0'}</div>
      )}

      <div className={cn(compact ? 'mt-auto pt-2' : 'mt-auto pt-3')}>
        <div className={cn(!compact && 'min-h-[3.25rem] flex flex-col justify-end')}>
          {product.oldPrice && (
            <div className={cn(
              'text-[var(--msa-ink-soft)]/70 line-through',
              compact ? 'text-[10.5px]' : 'min-h-[1.125rem] text-[11.5px]',
            )}
            >
              {formatUAH(product.oldPrice)}
            </div>
          )}
          <div className={cn(
            'font-display font-bold text-[var(--msa-ink)] leading-tight',
            compact ? 'text-[1rem]' : 'text-[1.05rem] sm:text-[1.2rem]',
          )}
          >
            {formatUAH(product.price)}
          </div>
        </div>
        <button
          onClick={() => addItem(product, 1)}
          disabled={outOfStock}
          title={outOfStock ? 'Товару немає в наявності' : undefined}
          className={cn(
            // Висота й ширина ті самі: неактивна кнопка мусить лишатись
            // такою ж мішенню, інакше натиск «провалюється» в картку.
            'w-full inline-flex items-center justify-center gap-1.5 rounded-full font-semibold transition-colors shrink-0',
            outOfStock
              ? 'bg-[var(--msa-bg-soft)] text-[var(--msa-ink-soft)] border border-[var(--msa-line)] cursor-not-allowed'
              : 'bg-[var(--msa-ink)] text-white hover:bg-[var(--msa-green-dark)]',
            compact ? 'mt-2 h-9 text-xs' : 'mt-3 h-11 text-sm gap-2',
          )}
        >
          {outOfStock ? 'Немає в наявності' : (<><ShoppingBag className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} /> До кошика</>)}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
